import React, { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import axios, { fehlerText, setzeAuthToken } from '../api/http';

// Typen
export type User = {
  id: number;
  name: string;
  email: string;
  role: string;
} | null;

type AuthContextType = {
  user: User;
  loading: boolean;
  initialLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  /** true für Admin und Projektleiter – bündelt die häufigste Rollenabfrage. */
  istLeitung: boolean;
  istAdmin: boolean;
};

const defaultAuthContext: AuthContextType = {
  user: null,
  loading: false,
  initialLoading: true,
  error: null,
  login: async () => false,
  logout: () => {},
  isAuthenticated: false,
  istLeitung: false,
  istAdmin: false
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Über eine Ref, damit der Interceptor unten immer die aktuelle Funktion
  // aufruft und nur einmal registriert werden muss.
  const logoutRef = useRef<() => void>(() => {});

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    setzeAuthToken(null);
  };

  logoutRef.current = logout;

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Nur 401 bedeutet "nicht (mehr) angemeldet".
        //
        // Vorher wurde auch bei 403 abgemeldet. Ein Berechtigungsfehler – etwa
        // beim Bearbeiten eines zu alten Eintrags – hat den Benutzer damit aus
        // der Anwendung geworfen, statt eine Meldung anzuzeigen.
        if (error.response?.status === 401 && localStorage.getItem('token')) {
          logoutRef.current();
          setError('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const loadUser = async (token: string): Promise<boolean> => {
    try {
      setzeAuthToken(token);

      const res = await axios.get('/api/users/me');

      setUser(res.data);
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      localStorage.removeItem('token');
      setzeAuthToken(null);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  // Beim Start prüfen, ob ein gültiges Token vorliegt
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        await loadUser(token);
      }

      setInitialLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post('/api/auth/login', { email, password });

      localStorage.setItem('token', res.data.token);
      const erfolgreich = await loadUser(res.data.token);

      if (!erfolgreich) {
        setError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
      }

      return erfolgreich;
    } catch (err) {
      setError(fehlerText(err, 'Login fehlgeschlagen'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        initialLoading,
        error,
        login,
        logout,
        isAuthenticated,
        istLeitung: user?.role === 'Admin' || user?.role === 'Projektleiter',
        istAdmin: user?.role === 'Admin'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
